<template>
  <PButton
    class="bz-button"
    :class="[size && `bz-button--${size}`, kind && `bz-button--${kind}`, { 'bz-button--block': block, 'bz-button--icon': isIconOnly }]"
    type="button"
    :label="label"
    :aria-label="ariaLabel"
    :severity="resolvedSeverity"
    :variant="resolvedVariant"
  >
    <template v-if="icon" #icon="{ class: iconClass }">
      <BzIcon :name="icon" :class="iconClass" data-bz-slot />
    </template>
    <!-- Content of its own (a spinner, a composed label) replaces the label, as PrimeVue's slot does. -->
    <template v-if="$slots.default" #default><slot /></template>
  </PButton>
</template>

<script>
import Button from 'primevue/button';
import BzIcon from './BzIcon.vue';

/** The design system's kinds of button, and how PrimeVue's Button is asked for each. */
const KINDS = {
  // The one action of a screen or an interruption: the verdict's blue.
  primary: { severity: 'primary', variant: undefined },
  // Any other action: the control grey.
  secondary: { severity: 'secondary', variant: undefined },
  // An alternative that must not compete with the primary.
  outlined: { severity: 'secondary', variant: 'outlined' },
  // A quiet action, a link.
  text: { severity: 'primary', variant: 'text' },
  // The one destructive action: red text, as both systems write it.
  danger: { severity: 'danger', variant: 'text' },
};

/**
 * A button of the design system: PrimeVue's Button in one of five kinds and four heights, its colours
 * in the preset (src/ui/preset.js) and its shape here.
 *
 * @rule   `kind`: primary · secondary · outlined · text · danger (KINDS above). `size`: `xl` 96 px for
 *         the action a whole screen exists for, `lg` 72 px for a primary action (norm 9, and the
 *         verdict's: 72 px at 22 px bold), `md` 56, `sm` 48, the floor of anything a finger taps. An icon
 *         button (`icon` and `ariaLabel`, no label) is a 48 px circle. Without a kind or a size the
 *         button is PrimeVue's with the app's tokens: what the screens not yet on the system (the
 *         login, Debug) still use, through `severity` and `variant`.
 * @rule   `icon` is a Material Symbol's name (src/ui/icons.js).
 * @why    Rounded rectangles on the service screen's radii (12 up to 56 px, 16 at 72, 20 at 96): the
 *         corner grows with the button, so a big one does not look like a small one stretched. Round only
 *         when the button is just an icon.
 * @why    The label is semibold and modest — 18 px on a 72 px button, 16 below — where the verdict paints
 *         22 px bold: a big target does not need a shout (feedback 2-oct-2026). The height stays: norm 9's
 *         72 px is for the finger, not for the type.
 * @why    The press is the app's own — App.vue scales every button to 0.96 and fades it to 0.85 for
 *         100 ms — so a library button and a service-screen button answer a finger the same way. Nothing
 *         here overrides it.
 * @rule   `type="button"` always: a library button inside a form must not submit it by accident.
 */
export default {
  name: 'BzButton',
  components: { PButton: Button, BzIcon },
  props: {
    // Required unless the button brings content of its own, or is an icon button.
    label: { type: String, default: undefined },
    kind: { type: String, default: undefined, validator: (k) => k in KINDS },
    size: { type: String, default: undefined, validator: (s) => ['xl', 'lg', 'md', 'sm'].includes(s) },
    icon: { type: String, default: undefined },
    ariaLabel: { type: String, default: undefined },
    block: { type: Boolean, default: false },
    // For the screens not yet on the system; a `kind` decides both.
    severity: { type: String, default: 'secondary' },
    variant: { type: String, default: undefined },
  },
  computed: {
    resolvedSeverity() {
      return this.kind ? KINDS[this.kind].severity : this.severity;
    },
    resolvedVariant() {
      return this.kind ? KINDS[this.kind].variant : this.variant;
    },
    isIconOnly() {
      return Boolean(this.icon && !this.label && !this.$slots.default);
    },
  },
  mounted() {
    if (process.env.NODE_ENV !== 'production' && !this.label && !this.$slots.default && !(this.icon && this.ariaLabel)) {
      console.warn('[BzButton] a button needs a label, content of its own, or an icon with an aria-label');
    }
  },
};
</script>

<style scoped>
.bz-button--block {
  display: flex;
  width: 100%;
}

/* A native button's line box — `normal`, not the page's 1.5 — so a button is as tall as the one
   it replaces (the screens not yet on the system). */
.bz-button {
  line-height: normal;
}

.bz-button :deep(.p-button-label) {
  line-height: normal;
}

/* ---- On the design system: a rounded rectangle, sized; round when it is only an icon. ---- */
.bz-button--xl,
.bz-button--lg,
.bz-button--md,
.bz-button--sm,
.bz-button--icon {
  font-family: var(--bz-font);
  font-weight: var(--bz-weight-label);
}

.bz-button--xl { min-height: var(--bz-target-hero); padding-inline: var(--bz-space-8); gap: var(--bz-space-3); border-radius: var(--bz-radius-xl); font-size: var(--bz-text-xl); letter-spacing: var(--bz-tracking-xl); }
.bz-button--lg { min-height: var(--bz-target-primary); padding-inline: var(--bz-space-7); gap: var(--bz-space-3); border-radius: var(--bz-radius-lg); font-size: var(--bz-text-lg); letter-spacing: var(--bz-tracking-lg); }
.bz-button--md { min-height: var(--bz-target-md); padding-inline: var(--bz-space-6); border-radius: var(--bz-radius-md); font-size: var(--bz-text-md); letter-spacing: var(--bz-tracking-md); }
.bz-button--sm { min-height: var(--bz-target-min); padding-inline: var(--bz-space-4); border-radius: var(--bz-radius-md); font-size: var(--bz-text-md); letter-spacing: var(--bz-tracking-md); }

.bz-button--icon {
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  padding: 0;
  border-radius: var(--bz-radius-full);
}

.bz-button--xl :deep(.p-button-icon) { font-size: var(--bz-icon-lg); }
.bz-button--lg :deep(.p-button-icon),
.bz-button--md :deep(.p-button-icon),
.bz-button--icon :deep(.p-button-icon) { font-size: var(--bz-icon-md); }
.bz-button--sm :deep(.p-button-icon) { font-size: var(--bz-icon-sm); }

/* Disabled: a filled button keeps a faint fill of the ink, every kind its label in the ink, faint. */
.bz-button--primary:disabled,
.bz-button--secondary:disabled {
  background: var(--bz-disabled-container);
  border-color: transparent;
}

.bz-button--primary:disabled,
.bz-button--secondary:disabled,
.bz-button--outlined:disabled,
.bz-button--text:disabled,
.bz-button--danger:disabled {
  color: var(--bz-disabled-ink);
  opacity: 1;
}
</style>
